import ExportIcon from "@heroicons/react/24/solid/CircleStackIcon";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Theme } from "@mui/system";
import CompletedRate from "assets/icons/completed_surveys_count.svg";
import CompletedCount from "assets/icons/completed_surveys_rate.svg";
import dayjs, { Dayjs } from "dayjs";
import { ECampaignSurveyType } from "pages/dashboard/CampaignDetails/questions/LeftSidebar/constants";
import {
  feedbackFilterTypesKeys,
  quickFilterOneTimeTypesFeedback,
} from "pages/dashboard/FeedBacks/constants";
import { useCallback, useEffect, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { handleDownload } from "shared/helpers";
import { getQueryParams } from "shared/helpers/getQueryParams";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import useTranslation from "shared/helpers/hooks/useTranslation";
import BasicAutocomplete from "shared/ui/Autocomplete";
import BasicRangePicker from "shared/ui/RangePicker";
import StyledToggleButton from "shared/ui/ToggleButton";
import { EDistributionMethods } from "store/enums/campaignDetails";
import { ERequestStatus } from "store/enums/index.enum";
import { ICampaign } from "store/interfaces/campaigns";
import { IDashboardDataRequest } from "store/interfaces/dashboard";
import { GetCampaigns, selectCampaigns } from "store/slicers/campaigns";
import { setLoading } from "store/slicers/common";
import {
  ExportStatisticsAll,
  GetDashboardData,
  GetDashboardGroupbyFilter,
  GetSurveyDeliveredData,
  selectDashboardData,
  selectDeliveredData,
} from "store/slicers/dashboard";
import {
  GetFeedbackFilterValues,
  selectFeedbackFilterValues,
} from "store/slicers/feedback";
import { GetDepartmentList, selectDepartmentsList } from "store/slicers/users";
import ScoreCharts from "../components/ScoreCharts";
import TrendChart from "../components/TrendChart";
import {
  defaultCampaignListRequestData,
  EDashboardExportMetricTypes,
} from "../constants";
import GroupbyFilter from "pages/dashboard/Home/components/GroupbyFilter";

interface IFormData {
  range: Array<Dayjs | null>;
  isOneTimeSurvey: string;
  campaignIDs: ICampaign[];
  distributionMethods?: { id: EDistributionMethods; name: string }[];
  employee: any;
  office: any;
}

const DashboardStatistics = () => {
  const lgUp = useMediaQuery<Theme>((theme) => theme.breakpoints.up("lg"));

  const filterValues = useSelector(selectFeedbackFilterValues);
  const dashboardData = useSelector(selectDashboardData);
  const deliveredData = useSelector(selectDeliveredData);
  const campaignList = useSelector(selectCampaigns);
  const departmentList = useSelector(selectDepartmentsList);
  const [isAutocompleteLoading, setAutocompleteLoading] = useState(false);
  // todo fix
  const [groupbyData, setGroupbyData] = useState([]);

  const dispatch = useAsyncDispatch();
  const t = useTranslation();
  const methods = useForm<IFormData>({
    defaultValues: {
      range: [null, null],
      isOneTimeSurvey: "0",
      campaignIDs: [],
      distributionMethods: [],
      employee: null,
      office: null,
    },
  });

  const { reset, getValues, control } = methods;

  const isOneTimeSurvey = useWatch({
    control,
    name: "isOneTimeSurvey",
  });

  const range = useWatch({
    control,
    name: "range",
  });

  const buttonDisabled = !(range?.[0] && range?.[1])

  const onExportScoreChartsData = async () => {
    const rangeDates = methods.getValues("range");
    if (rangeDates?.[0] || rangeDates?.[1]) {
      const formData: string = new URLSearchParams({
        EndDate: rangeDates?.[1]?.format("MM/DD/YYYY"),
        StartDate: rangeDates?.[0]?.format("MM/DD/YYYY"),
      }).toString();
      const { meta, payload } = await dispatch(
        ExportStatisticsAll({
          params: formData,
          ...(Number(isOneTimeSurvey)
            ? {
              body: {
                ...(Number(isOneTimeSurvey)
                  ? {
                    filters: [
                      {
                        key: feedbackFilterTypesKeys.ONE_TIME,
                        queryCondition: isOneTimeSurvey === "1" ? 2 : 3,
                        value: isOneTimeSurvey === "1" ? true : false,
                      },
                    ],
                  }
                  : {}),
              },
            }
            : {}),
        })
      );

      if (meta.requestStatus !== ERequestStatus.FULFILLED) {
        return;
      }
      handleDownload(payload, rangeDates);
    }
  };

  const getOfficeList = () => {
    dispatch(GetDepartmentList());
  };

  const getEmployeesList = async (val: string) => {
    const formData = {
      filter: "employee",
      term: val || "",
      count: 15,
    };

    await dispatch(GetFeedbackFilterValues(getQueryParams(formData)));
  };

  const getFilteredDashboardData = useCallback(
    async (dates?: Dayjs[], oneTimeValue?: string) => {
      if (dates) {
        const isOneTimeSurvey = oneTimeValue || getValues("isOneTimeSurvey");
        const employee = getValues("employee")?.id || null;
        const department = getValues("office")?.department || null;
        const data: IDashboardDataRequest = {
          filters: [
            {
              key: feedbackFilterTypesKeys.DATE,
              queryCondition: 4,
              value: dates?.[0].format("MM/DD/YYYY"),
            },
            {
              key: feedbackFilterTypesKeys.DATE,
              queryCondition: 5,
              value: dates?.[1].format("MM/DD/YYYY"),
            },
            ...(employee
              ? [
                {
                  key: feedbackFilterTypesKeys.EMPLOYEE,
                  queryCondition: 2,
                  value: employee,
                },
              ]
              : []),
            ...(department
              ? [
                {
                  key: feedbackFilterTypesKeys.BRANCH,
                  queryCondition: 1,
                  value: department,
                },
              ]
              : []),
            ...(Number(isOneTimeSurvey)
              ? [
                {
                  key: feedbackFilterTypesKeys.ONE_TIME,
                  queryCondition: isOneTimeSurvey === "1" ? 2 : 3,
                  value: isOneTimeSurvey === "1" ? true : false,
                },
              ]
              : []),
          ],
          campaignIDs: getValues("campaignIDs")?.map((campaign) => campaign.id),
          distributionMethods: getValues("distributionMethods")?.map(
            (method) => method.id
          ),
        };
        const dashboardDataReq = {
          ...data,
          userVisibility: 2,
        };

        dispatch(setLoading(true));
        await Promise.all([
          dispatch(GetSurveyDeliveredData(data)),
          dispatch(GetDashboardData(dashboardDataReq)),
          groupbyFilter(`${dates?.[0].format("MM-DD-YYYY")}...${dates?.[1].format("MM-DD-YYYY")}`)
        ]);
        dispatch(setLoading(false));
      }
    },
    [dispatch, getValues]
  );

  const groupbyFilter = async (date: string) => {
    const { meta, payload, error } = await dispatch(
      GetDashboardGroupbyFilter(date)
    );
    if (meta.requestStatus !== "fulfilled") {
      return;
    }
    setGroupbyData(payload)
  }

  const getCampaignList = async (search: string) => {
    try {
      setAutocompleteLoading(true);
      const data = {
        ...defaultCampaignListRequestData,
        searchText: search,
      };
      await dispatch(GetCampaigns(data));
    } catch (error) {
      setAutocompleteLoading(false);
    } finally {
      setAutocompleteLoading(false);
    }
  };

  const generateCustomKey = useCallback((option) => {
    return `${option?.department}-${option?.departmentName}`;
  }, []);

  const generateOptionLabel = useCallback((option) => {
    return `${option?.departmentName || "N/A"} (${option?.department || "N/A"
      })`;
  }, []);

  const init = useCallback(async () => {
    const dates = [dayjs().subtract(3, "month"), dayjs()];
    reset({
      range: dates,
      isOneTimeSurvey: "0",
    });
    getFilteredDashboardData(dates);
    await dispatch(GetCampaigns(defaultCampaignListRequestData));
  }, [dispatch, getFilteredDashboardData, reset]);

  useEffect(() => {
    init();
  }, [init]);

  return (
    <FormProvider {...methods}>
      <Box>
        <Box
          display="flex"
          alignItems={lgUp ? "center" : " left"}
          flexDirection={lgUp ? "row" : "column"}
          mb={2}
        >
          <Box
            flex={1}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              mb={1}
              variant="h4"
              fontWeight={500}
              color="text.secondary"
            >
              {t("overview_section_title")}
            </Typography>
            <Box>
              <Button
                variant="contained"
                onClick={onExportScoreChartsData}
                disabled={buttonDisabled}
                startIcon={<ExportIcon height={24} width={24} />}
              >
                <Typography>{t("export")}</Typography>
              </Button>
            </Box>
          </Box>
        </Box>
        <Grid
          container
          spacing={2}
          sx={{
            "& .MuiInputBase-root": {
              pt: "16px!important",
            },
          }}
        >
          <Grid item lg={3} md={6} sm={12} xs={12}>
            <Controller
              name="isOneTimeSurvey"
              defaultValue={"0"}
              render={({ field }) => {
                return (
                  <Box>
                    <ToggleButtonGroup {...field}>
                      {quickFilterOneTimeTypesFeedback?.map((type, index) => {
                        return (
                          <StyledToggleButton
                            key={index}
                            value={type.value}
                            selected={isOneTimeSurvey === type.value}
                            onClick={(e) => {
                              const target = e.target as HTMLButtonElement;
                              const value = target.getAttribute("value");
                              if (value === isOneTimeSurvey) {
                                e.preventDefault();
                              }
                              getFilteredDashboardData(
                                getValues("range"),
                                value || ""
                              );
                            }}
                          >
                            {type.label}
                          </StyledToggleButton>
                        );
                      })}
                    </ToggleButtonGroup>
                  </Box>
                );
              }}
            />
          </Grid>
          <Grid item lg={3} md={6} sm={12} xs={12}>
            <BasicRangePicker
              name="range"
              onSubmit={getFilteredDashboardData}
            />
          </Grid>
          <Grid item lg={3} md={6} sm={12} xs={12}>
            <BasicAutocomplete
              inputLabel={"Campaigns"}
              options={campaignList?.displayData || []}
              name={"campaignIDs"}
              defaultValue={[]}
              multiple
              optionLabel="name"
              async
              loading={isAutocompleteLoading}
              fetchFn={getCampaignList}
              onChangeCB={() => getFilteredDashboardData(getValues("range"))}
            />
          </Grid>
          <Grid item lg={3} md={6} sm={12} xs={12}>
            <BasicAutocomplete
              inputLabel={"Distribution methods"}
              options={[
                { id: EDistributionMethods.Email, name: "Email" },
                { id: EDistributionMethods.SMS, name: "SMS" },
                { id: EDistributionMethods.Push, name: "Push" },
                { id: EDistributionMethods.Default, name: "Default" },
              ]}
              name={"distributionMethods"}
              defaultValue={[]}
              multiple
              optionLabel="name"
              onChangeCB={() => getFilteredDashboardData(getValues("range"))}
            />
          </Grid>
          <Grid item lg={3} md={6} sm={12} xs={12}>
            <BasicAutocomplete
              inputLabel={"employee"}
              options={filterValues?.employee || []}
              name={"employee"}
              defaultValue={[]}
              optionLabel="label"
              loading={isAutocompleteLoading}
              async
              onChangeCB={() => getFilteredDashboardData(getValues("range"))}
              onFocus={() => getEmployeesList("")}
              fetchFn={getEmployeesList}
            />
          </Grid>
          <Grid item lg={3} md={6} sm={12} xs={12}>
            <BasicAutocomplete
              inputLabel={"office"}
              generateOptionLabel={generateOptionLabel}
              options={departmentList || []}
              name={"office"}
              defaultValue={[]}
              optionLabel="departmentName"
              loading={isAutocompleteLoading}
              onChangeCB={() => getFilteredDashboardData(getValues("range"))}
              onFocus={() => getOfficeList()}
              generateCustomKey={generateCustomKey}
              customFilter={(
                options: { department: string; departmentName: string }[],
                input
              ) => {
                return options.filter(
                  (opt) =>
                    opt?.department?.toLowerCase().includes(input) ||
                    opt?.departmentName?.toLowerCase().includes(input)
                );
              }}
            />
          </Grid>
        </Grid>

        <Box gap={4} display="flex" py={2} flexWrap={"wrap"}>
          <Box flex={1}>
            <Card sx={{ minHeight: 133 }}>
              <CardContent>
                {deliveredData && (
                  <Box display="flex" justifyContent="space-around">
                    <Box display="flex">
                      <img src={CompletedRate} alt="rate" />
                      <Box ml={1}>
                        <Typography fontSize={14}>
                          {t("overview_surveys_completed")}
                        </Typography>
                        <Typography fontSize={32}>
                          {deliveredData?.responded}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex">
                      <img src={CompletedCount} alt="count" />
                      <Box ml={1}>
                        <Typography fontSize={14}>
                          {t("overview_response_rate")}
                        </Typography>
                        <Typography fontSize={32}>
                          {deliveredData?.opened &&
                            deliveredData?.responded &&
                            Math.floor(
                              (deliveredData?.responded /
                                deliveredData?.delivered) *
                              100
                            )}
                          %
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
          <Box flex={1}>
            <Card sx={{ padding: "5px", minHeight: 133 }}>
              <CardContent>
                {deliveredData && (
                  <Box display="flex" justifyContent="space-between">
                    <Box>
                      <Typography fontSize={14}>
                        {t("overview_sent")}
                      </Typography>
                      <Typography
                        fontSize={32}
                        sx={{ lineHeight: "38px", color: "#007AFF" }}
                      >
                        {deliveredData?.sent}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography fontSize={14}>
                        {t("overview_delivered")}
                      </Typography>
                      <Typography
                        fontSize={32}
                        sx={{ lineHeight: "38px", color: "#643DC7" }}
                      >
                        {deliveredData?.delivered}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography fontSize={14}>
                        {t("overview_opened")}
                      </Typography>
                      <Typography
                        fontSize={32}
                        sx={{ lineHeight: "38px", color: "#A13AC6" }}
                      >
                        {deliveredData?.opened}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography fontSize={14}>
                        {t("overview_bounced")}
                      </Typography>
                      <Typography
                        fontSize={32}
                        sx={{ lineHeight: "38px", color: "#DD3A97" }}
                      >
                        {deliveredData?.bounced}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>

        <Box>
          {!(getValues("campaignIDs")?.length > 0) && (
            <Box display="flex" gap={3}>
              <Box flex={1}>
                <GroupbyFilter metric={"nps"} label={"Group by Campaign NPS"} response={groupbyData} />
              </Box>
              <Box flex={1}>
                <GroupbyFilter metric={"effortScore"} label={"Group by Campaign CES"} response={groupbyData} />
              </Box>
            </Box>
          )}
          {dashboardData?.nps && (
            <ScoreCharts
              label={t("dashboard_nps_key")}
              dashboardDataChopChart={dashboardData?.npsChopChart}
              scoreData={dashboardData?.nps}
              type={ECampaignSurveyType.Nps}
              metricType={EDashboardExportMetricTypes.NPS}
              rangeDates={methods.watch("range")}
              oneTimeValue={isOneTimeSurvey}
            />
          )}
          {dashboardData?.friendliness && (
            <ScoreCharts
              label={t("dashboard_friendliness_key")}
              dashboardDataChopChart={dashboardData?.friendlinessChopChart}
              scoreData={dashboardData?.friendliness}
              type={ECampaignSurveyType.ServiceQualityScore}
              metricType={EDashboardExportMetricTypes.eNPS}
              rangeDates={methods.watch("range")}
              oneTimeValue={isOneTimeSurvey}
            />
          )}
          {dashboardData?.effortScore && (
            <ScoreCharts
              label={t("dashboard_effort_key")}
              dashboardDataChopChart={dashboardData?.effortScoreChopChart}
              scoreData={dashboardData?.effortScore}
              type={ECampaignSurveyType.CustomerEffortScore}
              metricType={EDashboardExportMetricTypes.CES}
              rangeDates={methods.watch("range")}
              oneTimeValue={isOneTimeSurvey}
            />
          )}

          {dashboardData?.satisfactionScore && (
            <ScoreCharts
              label={t("dashboard_satisfaction_key")}
              dashboardDataChopChart={dashboardData?.satisfactionScoreChopChart}
              scoreData={dashboardData?.satisfactionScore}
              type={ECampaignSurveyType.CustomerSatisfactionScore}
              metricType={EDashboardExportMetricTypes.CSAT}
              rangeDates={methods.watch("range")}
              oneTimeValue={isOneTimeSurvey}
            />
          )}
          <TrendChart chartsData={dashboardData?.lineChartData} />
        </Box>
      </Box>
    </FormProvider>
  );
};

export default DashboardStatistics;
